import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { gql, useMutation } from '@apollo/client';
import {
  useAllAlerts,
  usePropertyAlerts,
  useAlertsManager,
  ActiveAlert,
} from '../../system/AlertsManager';
import DataTable from '../shared/data-table';
import { FilterList } from '../alerts/ui/filter-list';
import {
  GridColDef,
  GridRowParams,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  MobileListCard,
  MobileListDataTableProps,
} from '../shared/mobile-list-card';
import {
  Typography,
  useTheme,
  Box,
  CircularProgress,
  Button,
} from '@mui/material';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import { Notifier } from '../../system/services/notificationManager';

const FORCE_RESOLVE_ALERT_MUTATION = gql`
  mutation ForceResolveAlert($_id: String!) {
    forceResolveAlert(_id: $_id) {
      success
      message
    }
  }
`;

interface AlertsListProps {
  propertyId?: string;
}

interface AlertRow {
  actions?: string;
  deviceId: string;
  deviceName: string;
  deviceType: string;
  id: string;
  propertyId: string;
  propertyName: string;
  severity: string;
  timestamp: number;
  type: string;
  unitId?: string;
  unitName?: string;
}
export const AlertsList: React.FC<AlertsListProps> = ({ propertyId }) => {
  const navigate = useNavigate();
  const { state: specificDevices } = useLocation();
  const isSmall = useIsSmallScreen();

  const allAlerts = useAllAlerts();
  const { alerts: propertyAlerts, loading: propertyAlertsLoading } =
    usePropertyAlerts(propertyId ?? '');
  const { loading: allAlertsLoading } = useAlertsManager();

  const loading = propertyId ? propertyAlertsLoading : allAlertsLoading;

  const theme = useTheme();
  const { can } = useAuthorizer();
  const [forceResolveAlert] = useMutation(FORCE_RESOLVE_ALERT_MUTATION);

  const handleResolveAlert = async (alertId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await forceResolveAlert({
        variables: { _id: alertId },
      });
      const data = result.data?.forceResolveAlert;
      if (data?.success) {
        Notifier.success(data.message);
      } else {
        Notifier.error(data?.message ?? 'Failed to resolve alert');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve alert';
      Notifier.error(message);
    }
  };

  const showActionsColumn =
    !isSmall &&
    (can as (action: string, subject: string) => boolean)(
      'resolveAlert',
      'Alert',
    );

  const [selectedAlertType, setSelectedAlertType] =
    React.useState<string>('all');

  const alerts = propertyId ? propertyAlerts : allAlerts;

  // Filter alerts by selected type only (DataTable handles search and advanced filtering)
  const filteredAlerts = React.useMemo(() => {
    if (selectedAlertType === 'all') {
      return alerts;
    }
    return alerts.filter((alert) => {
      const alertTypeId = alert.type?.id;
      switch (selectedAlertType) {
        case 'SOURCE_OFFLINE':
          return alertTypeId === 'SOURCE_OFFLINE';
        case 'DEVICE_OFFLINE':
          return alertTypeId === 'DEVICE_OFFLINE';
        case 'THRESHOLD_ALERTS':
          // Include all threshold-based alerts (temperature, humidity, pressure limits)
          return [
            'HIGH_TEMP',
            'LOW_TEMP',
            'HIGH_RH',
            'LOW_RH',
            'HIGH_DHW_TEMP',
            'LOW_DHW_TEMP',
            'HIGH_SUPPLY_TEMP',
            'LOW_SUPPLY_TEMP',
            'HIGH_RETURN_TEMP',
            'LOW_RETURN_TEMP',
            'HIGH_SUPPLY_PSI',
            'LOW_SUPPLY_PSI',
            'HIGH_RETURN_PSI',
            'LOW_RETURN_PSI',
          ].includes(alertTypeId || '');
        case 'LEAK_DETECTED':
          return alertTypeId === 'LEAK_DETECTED';
        case 'EQUIPMENT_ALARM':
          return alertTypeId === 'EQUIPMENT_ALARM';
        default:
          return true;
      }
    });
  }, [alerts, selectedAlertType]);

  // Use the same pagination logic as users list - enable pagination when there are more than 50 alerts
  const needsPagination = filteredAlerts.length > 50;

  const handleRowClick = (row: AlertRow) => {
    const alertType = row.type;
    const propertyId = row.propertyId;
    const unitId = row.unitId;
    const deviceId = row.deviceId;

    switch (alertType) {
      case 'Source Offline':
        navigate(`/sources/${deviceId}/edit-source`);
        break;
      default:
        navigate(`/properties/${propertyId}/units/${unitId}`);
        break;
    }
  };

  /**
   * formatRows
   * remap query data to match the 'AlertRow' format
   * @returns array of alert table rows - AlertRow[]
   * @param data - query data
   * @param _filter - used to pare down the list if specific devices were passed in using the route params state object
   */
  function formatRows(data: ActiveAlert[], _filter?: string[]): AlertRow[] {
    const _data = data
      .filter(
        (dataRow) =>
          !_filter ||
          !_filter.length ||
          _filter?.includes(dataRow.device?.id ?? ''),
      )
      .map((dataRow) => {
        return {
          id: dataRow._id,
          actions: '',
          propertyName: dataRow.property?.name ?? '',
          unitName: dataRow.unit?.name ?? '',
          deviceName: dataRow.device?.name ?? '',
          deviceType: dataRow.device?.type ?? '',
          severity: dataRow.severity ?? '',
          timestamp: dataRow.initiated ?? -1,
          type: dataRow.type?.name ?? '',
          deviceId: dataRow.device?.id ?? '',
          propertyId: dataRow.property?.id ?? '',
          unitId: dataRow.unit?.id ?? '',
        } as AlertRow;
      });
    return [..._data];
  }

  const actionsColumn: GridColDef = {
    field: 'actions',
    headerName: 'Actions',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<Partial<any>>) => {
      const alertSubject = {
        __typename: 'Alert' as const,
        _id: params.row.id,
      };
      const canResolve = (
        can as (action: string, subject: typeof alertSubject) => boolean
      )('resolveAlert', alertSubject);
      if (!canResolve) return null;
      return (
        <Button
          size="small"
          color="secondary"
          variant="contained"
          sx={{ color: 'white' }}
          onClick={(e) => handleResolveAlert(params.row.id, e)}
        >
          Resolve Alert <RefreshIcon style={{ color: 'white', fontSize: 18 }} />
        </Button>
      );
    },
  };

  const typeColumn = isSmall
    ? ({
        field: 'type',
        headerName: 'Type',
        align: 'left',
        flex: 1,
        renderCell: (params: GridRenderCellParams<Partial<any>>) => {
          // Get alert styling based on severity
          const getAlertTextColor = (severity: string) => {
            switch (severity) {
              case 'major':
                return '#ff0000'; // Red for major alerts
              case 'threshold':
                return '#ed6c02'; // Orange for threshold alerts
              case 'normal':
                return '#000000'; // Black for normal alerts
              default:
                return 'text.primary'; // Default theme color
            }
          };

          const alertTextColor = getAlertTextColor(params.row.severity);
          const alertSubject = {
            __typename: 'Alert' as const,
            _id: params.row.id,
          };
          const canResolve = (
            can as (action: string, subject: typeof alertSubject) => boolean
          )('resolveAlert', alertSubject);

          return (
            <MobileListCard cardSx={{ mb: 1 }}>
              <Box component="div">
                <Typography
                  component="h5"
                  sx={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: alertTextColor,
                  }}
                >
                  {params.value}
                </Typography>
                <Typography
                  component="div"
                  color="text.secondary"
                  sx={{ fontSize: 16 }}
                >
                  <b>Device:</b> {params.row.deviceName}
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontSize: 16 }}
                  color="text.secondary"
                >
                  <b>Type:</b> {params.row.deviceType}
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontSize: 16 }}
                  color="text.secondary"
                >
                  <b>Since:</b> {dayjs(params.row.timestamp).format('l LT')}
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontSize: 16 }}
                  color="text.secondary"
                >
                  <b>Location:</b>{' '}
                  {`${params.row.propertyName ?? ''}${params.row.propertyName && params.row.unitName ? ' / ' : ''}${params.row.unitName ?? ''}${!params.row.propertyName && !params.row.unitName ? 'Not specified' : ''}`}
                </Typography>
                {canResolve ? (
                  <Button
                    size="small"
                    color="secondary"
                    variant="contained"
                    sx={{ width: '100%', color: 'white', fontSize: 14 }}
                    onClick={(e) => handleResolveAlert(params.row.id, e)}
                  >
                    One-Click Resolve Alert
                    <RefreshIcon />
                  </Button>
                ) : null}
              </Box>
            </MobileListCard>
          );
        },
      } as GridColDef)
    : ({
        field: 'type',
        headerName: 'Type',
        flex: 1,
        renderCell: (params: GridRenderCellParams<Partial<any>>) => {
          // Get alert styling based on severity
          const getAlertTextColor = (severity: string) => {
            switch (severity) {
              case 'major':
                return '#ff0000'; // Red for major alerts
              case 'threshold':
                return '#ed6c02'; // Orange for threshold alerts
              case 'normal':
                return '#000000'; // Black for normal alerts
              default:
                return '#757575'; // Gray for unknown/empty severity
            }
          };

          const alertTextColor = getAlertTextColor(params.row.severity);

          return (
            <Typography
              sx={{
                color: alertTextColor,
                fontWeight: params.row.severity === 'major' ? 'bold' : 'normal',
                borderLeftWidth: '6px',
                borderLeftStyle: 'solid',
                borderLeftColor: alertTextColor,
                paddingLeft: '4px',
              }}
            >
              {params.value}
            </Typography>
          );
        },
      } as GridColDef);

  const columns = [
    { ...typeColumn },
    { field: 'deviceName', headerName: 'Device Name', flex: 1 },
    { field: 'deviceType', headerName: 'Device Type', flex: 1 },
    { field: 'propertyName', headerName: 'Property Name', flex: 1 },
    { field: 'unitName', headerName: 'Unit', flex: 1 },
    {
      field: 'timestamp',
      headerName: 'First Reported',
      flex: 1,
      valueFormatter: (params: any) => {
        const date = params.value;
        return dayjs(date).format('l LT');
      },
    },
    { ...actionsColumn },
  ];

  const formattedRows = formatRows(filteredAlerts, specificDevices);

  // Custom empty state component for filtered alerts
  const CustomNoRowsOverlay = () => {
    const getFilterMessage = () => {
      switch (selectedAlertType) {
        case 'SOURCE_OFFLINE':
          return 'No source offline alerts detected for your properties.';
        case 'DEVICE_OFFLINE':
          return 'No devices offline alerts detected for your properties.';
        case 'THRESHOLD_ALERTS':
          return 'No threshold alerts detected for your properties.';
        case 'LEAK_DETECTED':
          return 'No leak detection alerts reported for your properties.';
        case 'EQUIPMENT_ALARM':
          return 'No equipment alarms detected for your properties.';
        default:
          return 'Good News! No alerts detected for your properties.';
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          color: 'text.secondary',
          textAlign: 'center',
          padding: 1,
        }}
      >
        <Typography
          sx={{
            mb: 1,
            fontWeight: 'bold',
            fontSize: '1.5em',
          }}
        >
          {getFilterMessage()}
        </Typography>
        <Typography variant="body2">
          {selectedAlertType === 'all'
            ? 'Good News! There are currently no alerts for your properties.'
            : 'Select a different "Alert Type" to explore possible alerts.'}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Scanning your properties for any alerts...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        alignItems: 'stretch',
        cursor: 'pointer',
        height: needsPagination ? 'calc(100vh - 200px)' : 'auto',
      }}
    >
      <FilterList
        theme={theme}
        selectedAlertType={selectedAlertType}
        handleAlertTypeSelect={setSelectedAlertType}
      />
      <DataTable
        {...(isSmall ? MobileListDataTableProps : {})}
        getRowId={(row: AlertRow) => row.id}
        additionalProps={{
          onRowClick: (params: GridRowParams) => {
            handleRowClick(params.row as AlertRow);
          },
          slots: {
            noRowsOverlay: CustomNoRowsOverlay,
          },
        }}
        columns={columns}
        suppressAutoPageSize={true}
        rowHeight={isSmall ? 180 : undefined}
        columnVisibilityModel={{
          actions: showActionsColumn,
          deviceName: !isSmall,
          deviceType: !isSmall,
          propertyName: !isSmall,
          unitName: !isSmall,
          timestamp: !isSmall,
        }}
        rows={formattedRows}
        hideFooter={false}
        hideFilterSorting={false}
        hideFilterButton={false}
        hideTextSearch={false}
        pageSize={50}
        pageSizeOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        loading={loading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'timestamp', sort: 'desc' }],
          },
        }}
      />
    </div>
  );
};
